import { useDependencies } from '@servicetitan/react-ioc';
import { FinancingApi, Provider } from '@servicetitan/financing-api-client';
import { useQuery } from '@tanstack/react-query';
import { SetupDataStore } from '../stores/setupDataStore';

/**
 * Hook to check if a specific provider is enabled for a business unit
 * This checks both tenant-level enablement AND business-unit-level enablement
 *
 * @param provider - The provider to check (e.g., 'Turns', 'GoodLeap', etc.)
 * @returns true if the provider is enabled at tenant level, and if buId is provided in setupData, also at BU level
 * @returns false if the provider is disabled at tenant level or explicitly disabled for the business unit
 *
 * Note: Uses setupData.tenantId and setupData.buId for consistency. If no buId is provided in setupData, only tenant-level enablement is checked
 */
/**
 * Same enablement check as {@link useIsProviderEnabledForBusinessUnit}, but also surfaces the
 * underlying query's loading state so a readiness gate can wait for it.
 *
 * @returns `isEnabled` (tenant + BU enablement) and `isLoading` (true only while the
 * provider-preference query is loading for the first time; false when disabled or errored).
 */
export const useProviderEnabledForBusinessUnitResult = (
    provider: Provider,
): { isEnabled: boolean; isLoading: boolean } => {
    const { setupData } = SetupDataStore.useStore();
    const buId = setupData?.buId;
    const tenantId = setupData?.tenantId;

    const [apiClient] = useDependencies(FinancingApi);

    const providerName = provider?.charAt(0)?.toUpperCase() + provider?.slice(1);

    const providerPreferenceQuery = useQuery({
        queryKey: ['providerPreference', tenantId, providerName],
        queryFn: async () => {
            try {
                return await apiClient.tenantsGetProviderPreference(
                    tenantId!,
                    providerName as Provider,
                );
            } catch (error: any) {
                if (error?.response?.status === 404) {
                    return {
                        data: {
                            enabled: false,
                            preferred: false,
                            disabledBUs: [] as string[],
                        },
                    };
                }
                throw error;
            }
        },
        enabled: !!tenantId && !!providerName,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });

    const providerPreference = providerPreferenceQuery.data;

    // Check if provider is enabled at tenant level
    const isEnabledAtTenantLevel = providerPreference?.data?.enabled ?? false;

    // Check if business unit is NOT in the disabled list
    // If no buId is provided, skip business unit validation and just check tenant level
    const isEnabledForBusinessUnit = buId
        ? !(providerPreference?.data?.disabledBUs?.includes(buId) ?? false)
        : true; // No buId means we can't validate at BU level, so default to true

    return {
        // Provider must be enabled at tenant level, and if buId is provided, also at BU level
        isEnabled: isEnabledAtTenantLevel && isEnabledForBusinessUnit,
        isLoading: providerPreferenceQuery.isLoading,
    };
};

export const useIsProviderEnabledForBusinessUnit = (provider: Provider): boolean =>
    useProviderEnabledForBusinessUnitResult(provider).isEnabled;
